import { useEffect, useState } from 'react';

/**
 * Toggle global "modo preview" — baja resolución / baja calidad / costo bajo
 * para iterar barato antes de la versión final.
 *
 * Persistido en localStorage. Cuando está ON, los handlers de generación
 * (xvision-* en backend) reciben `preview: true` en la metadata del mensaje
 * y ajustan parámetros: resolution más chica, modelos más baratos
 * (ej. Live Avatar $0.01/s en vez de Kling V2 Pro $0.115/s para lipsync),
 * menos inference steps, etc.
 *
 * Cada agente xvision-* documenta en su prompt qué hace en modo preview.
 *
 * Listeners cross-tab: si cambia en una pestaña, otras pestañas reaccionan
 * vía evento `storage`. Útil cuando el operador tiene varios chats abiertos.
 */

const STORAGE_KEY = 'xlv:preview-mode';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === '1';
}

export function usePreviewMode() {
  const [enabled, setEnabledState] = useState<boolean>(readInitial);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setEnabledState(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setEnabled = (next: boolean) => {
    setEnabledState(next);
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  };

  const toggle = () => setEnabled(!enabled);

  return { enabled, setEnabled, toggle };
}

/**
 * Helper para uso fuera de React (ej: incluir en payload de un fetch al backend).
 */
export function getPreviewMode(): boolean {
  return readInitial();
}
